import { useCallback, useMemo } from "react";
import type { View } from "@common/types/views/views";
import type { ColumnData, ColumnSettingsConfig } from "./columnSettingsUtils";

/** `columns` split into the fixed ones, in `fixedColumnIds` order, and the rest in order. */
const splitFixedColumns = (
    columns: ColumnData[],
    fixedColumnIds: ReadonlySet<string>,
) => {
    const fixedOrder = [...fixedColumnIds];
    return {
        fixedColumns: columns
            .filter(({ id }) => fixedColumnIds.has(id))
            .toSorted(
                (first, second) =>
                    fixedOrder.indexOf(first.id) -
                    fixedOrder.indexOf(second.id),
            ),
        orderedColumns: columns.filter(({ id }) => !fixedColumnIds.has(id)),
    };
};

const toColumnData = (
    id: string,
    config: ColumnSettingsConfig,
): ColumnData => ({
    id,
    name: config.name,
    internal: config.internal ?? false,
    immutable: config.immutable ?? false,
    required: config.required ?? false,
    hint: config.hint,
});

interface UseColumnSettingsRowsArgs {
    columns: View["columns"];
    columnConfigs: Readonly<Record<string, ColumnSettingsConfig>>;
    fixedColumnIds: ReadonlySet<string>;
    onColumnsChange: (columns: View["columns"]) => void;
}

/**
 * The rows of a column settings list: the shown columns in view order and
 * the hidden ones in config order, each split into fixed and ordered rows,
 * plus the eye's toggle (a shown column comes back at the end).
 */
export const useColumnSettingsRows = ({
    columns,
    columnConfigs,
    fixedColumnIds,
    onColumnsChange,
}: UseColumnSettingsRowsArgs) => {
    const configsById = useMemo(
        () => new Map(Object.entries(columnConfigs)),
        [columnConfigs],
    );

    const visibleColumns = useMemo(
        (): ColumnData[] =>
            columns.flatMap((column) => {
                const config = configsById.get(column.id);
                return config === undefined || config.hiddenFromSettings
                    ? []
                    : [
                          {
                              ...toColumnData(column.id, config),
                              options: column.options ?? undefined,
                          },
                      ];
            }),
        [columns, configsById],
    );

    const hiddenColumns = useMemo(() => {
        const visibleIds = new Set(columns.map((column) => column.id));
        return [...configsById]
            .filter(
                ([id, config]) =>
                    !visibleIds.has(id) &&
                    !(config.internal ?? false) &&
                    !(config.hiddenFromSettings ?? false),
            )
            .map(([id, config]) => toColumnData(id, config));
    }, [columns, configsById]);

    const shown = useMemo(
        () => splitFixedColumns(visibleColumns, fixedColumnIds),
        [visibleColumns, fixedColumnIds],
    );
    const hidden = useMemo(
        () => splitFixedColumns(hiddenColumns, fixedColumnIds),
        [hiddenColumns, fixedColumnIds],
    );

    const toggleVisibility = useCallback(
        (columnId: string) => {
            const config = configsById.get(columnId);
            if (config === undefined) {
                return;
            }
            if (!columns.some((column) => column.id === columnId)) {
                onColumnsChange([...columns, { id: columnId, options: {} }]);
                return;
            }
            if (!config.immutable && !config.required) {
                onColumnsChange(
                    columns.filter((column) => column.id !== columnId),
                );
            }
        },
        [columns, configsById, onColumnsChange],
    );

    return {
        hasShownColumns: visibleColumns.length > 0,
        hasHiddenColumns: hiddenColumns.length > 0,
        shown,
        hidden,
        toggleVisibility,
    };
};
